<template>
  <HrPageLayout title="Holidays" subtitle="Manage public and company holidays" icon="🎄">

    <div class="hol-page">

      <!-- ── Top Toolbar ── -->
      <div class="hol-toolbar">
        <div class="hol-tabs">
          <button class="hol-tab" :class="{ 'hol-tab-active': activeTab === 'list' }" @click="activeTab = 'list'">
            <i class="bx bx-list-ul"></i><span>Holidays</span>
            <span v-if="metrics.totalHolidays" class="hol-tab-count">{{ metrics.totalHolidays }}</span>
          </button>
          <button class="hol-tab" :class="{ 'hol-tab-active': activeTab === 'calendar' }" @click="activeTab = 'calendar'; $nextTick(initCalendar)">
            <i class="bx bx-calendar"></i><span>Calendar</span>
          </button>
          <button class="hol-tab" :class="{ 'hol-tab-active': activeTab === 'categories' }" @click="activeTab = 'categories'; getHolidayCategories()">
            <i class="bx bx-tag"></i><span>Categories</span>
          </button>
          <button class="hol-tab" :class="{ 'hol-tab-active': activeTab === 'statistics' }" @click="activeTab = 'statistics'; loadStatistics()">
            <i class="bx bx-bar-chart-alt-2"></i><span>Statistics</span>
          </button>
        </div>
        <div class="hol-toolbar-right">
          <div class="hol-year-select">
            <i class="bx bx-calendar"></i>
            <select v-model.number="filters.year" @change="getHolidays">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <button class="hol-btn hol-btn-ghost" @click="refreshData"><i class="bx bx-refresh"></i><span>Refresh</span></button>
          <button class="hol-btn hol-btn-outline" @click="exportHolidays"><i class="bx bx-download"></i><span>Export</span></button>
          <button class="hol-btn hol-btn-primary" @click="openForm()"><i class="bx bx-plus"></i><span>Add Holiday</span></button>
        </div>
      </div>

      <!-- ── Summary Cards ── -->
      <div class="hol-summary-row">
        <div class="hol-summary-card" style="--accent:#3b82f6">
          <div class="hol-summary-icon" style="background:#eff6ff;color:#3b82f6"><i class="bx bx-calendar-check"></i></div>
          <div><span class="hol-summary-label">Total Holidays</span><span class="hol-summary-value">{{ metrics.totalHolidays || 0 }}</span></div>
        </div>
        <div class="hol-summary-card" style="--accent:#059669">
          <div class="hol-summary-icon" style="background:#f0fdf4;color:#059669"><i class="bx bx-world"></i></div>
          <div><span class="hol-summary-label">Public Holidays</span><span class="hol-summary-value">{{ metrics.publicHolidays || 0 }}</span></div>
        </div>
        <div class="hol-summary-card" style="--accent:#0ea5e9">
          <div class="hol-summary-icon" style="background:#f0f9ff;color:#0ea5e9"><i class="bx bx-repeat"></i></div>
          <div><span class="hol-summary-label">Fixed (Annual)</span><span class="hol-summary-value">{{ metrics.fixedHolidays || 0 }}</span></div>
        </div>
        <div class="hol-summary-card" style="--accent:#f59e0b">
          <div class="hol-summary-icon" style="background:#fffbeb;color:#f59e0b"><i class="bx bx-calendar-alt"></i></div>
          <div><span class="hol-summary-label">Months Covered</span><span class="hol-summary-value">{{ metrics.holidayMonths || 0 }}<small>/12</small></span></div>
        </div>
      </div>

      <!-- ══ TAB: LIST ══ -->
      <template v-if="activeTab === 'list'">
        <!-- Filters -->
        <div class="hol-card hol-filters-card">
          <div class="hol-filters-row">
            <div class="hol-filter-group">
              <label>Month</label>
              <select v-model="filters.month" @change="getHolidays" class="hol-select">
                <option value="">All Months</option>
                <option v-for="(m, i) in months" :key="i" :value="i+1">{{ m }}</option>
              </select>
            </div>
            <div class="hol-filter-group">
              <label>Category</label>
              <select v-model="filters.categoryId" @change="getHolidays" class="hol-select">
                <option value="">All Categories</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.category_name }}</option>
              </select>
            </div>
            <div class="hol-filter-group">
              <label>Type</label>
              <select v-model="filters.isPublic" @change="getHolidays" class="hol-select">
                <option value="">All Types</option>
                <option value="1">Public</option>
                <option value="0">Non-Public</option>
              </select>
            </div>
            <div class="hol-filter-group hol-filter-search">
              <label>Search</label>
              <div class="hol-search-wrap">
                <i class="bx bx-search"></i>
                <input v-model="filters.search" @input="handleSearch" class="hol-input" placeholder="Search holidays...">
              </div>
            </div>
            <button class="hol-btn hol-btn-ghost hol-filter-reset" @click="resetFilters"><i class="bx bx-x"></i> Reset</button>
          </div>
        </div>

        <!-- Table -->
        <div class="hol-card">
          <div class="hol-card-header">
            <span class="hol-card-title"><i class="bx bx-list-ul"></i> Holiday List</span>
            <span class="hol-row-count">{{ holidays.length }} holiday{{ holidays.length !== 1 ? 's' : '' }}</span>
          </div>
          <div v-if="loading" class="hol-loading"><div class="hol-spinner"></div><span>Loading...</span></div>
          <div v-else-if="!holidays.length" class="hol-empty">
            <i class="bx bx-calendar-x"></i><h5>No holidays found</h5>
            <p>Try adjusting your filters or add a new holiday.</p>
            <button class="hol-btn hol-btn-primary" @click="openForm()"><i class="bx bx-plus"></i> Add Holiday</button>
          </div>
          <div v-else class="hol-table-wrap">
            <table class="hol-table">
              <thead>
                <tr><th>#</th><th>Holiday</th><th>Date</th><th>Type</th><th>Categories</th><th>Actions</th></tr>
              </thead>
              <tbody>
                <tr v-for="(h, idx) in holidays" :key="h.id">
                  <td class="hol-td-num">{{ idx + 1 }}</td>
                  <td>
                    <div class="hol-name-cell">
                      <div class="hol-dot" :style="{ background: getCategoryFirstColor(h.category_colors) }"></div>
                      <div>
                        <span class="hol-name">{{ h.holiday_name }}</span>
                        <span v-if="h.description" class="hol-desc">{{ truncateText(h.description, 50) }}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="hol-date">{{ formatDate(h.holiday_date) }}</span>
                    <span v-if="isHolidayToday(h.holiday_date)" class="hol-pill hol-pill-today">Today</span>
                    <span v-else-if="isHolidaySoon(h.holiday_date)" class="hol-pill hol-pill-soon">Soon</span>
                  </td>
                  <td>
                    <span class="hol-pill" :class="h.is_public == 1 ? 'hol-pill-public' : 'hol-pill-private'">
                      {{ h.is_public == 1 ? 'Public' : 'Non-Public' }}
                    </span>
                    <span v-if="h.is_fixed == 1" class="hol-pill hol-pill-fixed">Fixed</span>
                  </td>
                  <td>
                    <div class="hol-cat-tags" v-if="h.categories">
                      <span v-for="(cat, ci) in getCategoryList(h.categories)" :key="ci"
                            class="hol-cat-tag" :style="{ background: getCategoryColor(h.category_colors, ci) }">
                        {{ cat }}
                      </span>
                    </div>
                    <span v-else class="hol-no-cat">—</span>
                  </td>
                  <td>
                    <div class="hol-row-actions">
                      <button class="hol-action-btn hol-action-view" @click="viewHolidayDetails(h)" title="View"><i class="bx bx-show"></i></button>
                      <button class="hol-action-btn hol-action-edit" @click="editHoliday(h)" title="Edit"><i class="bx bx-edit"></i></button>
                      <button class="hol-action-btn hol-action-delete" @click="deleteHoliday(h)" title="Delete"><i class="bx bx-trash"></i></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <!-- ══ TAB: CALENDAR ══ -->
      <template v-if="activeTab === 'calendar'">
        <div class="hol-cal-layout">
          <!-- Calendar main -->
          <div class="hol-card hol-cal-main">
            <div class="hol-card-header">
              <span class="hol-card-title"><i class="bx bx-calendar"></i> Holiday Calendar {{ filters.year }}</span>
              <div class="hol-cal-view-btns">
                <button class="hol-view-btn" :class="{ 'hol-view-btn-active': calendarView === 'month' }" @click="setCalView('month')">Month</button>
                <button class="hol-view-btn" :class="{ 'hol-view-btn-active': calendarView === 'year' }" @click="setCalView('year')">Year</button>
              </div>
            </div>
            <div class="hol-calendar-wrap">
              <div id="holidays-calendar"></div>
            </div>
          </div>
          <!-- Upcoming sidebar -->
          <div class="hol-cal-sidebar">
            <div class="hol-card">
              <div class="hol-card-header">
                <span class="hol-card-title"><i class="bx bx-time-five"></i> Upcoming</span>
              </div>
              <div v-if="!upcomingHolidays.length" class="hol-empty hol-empty-sm">
                <i class="bx bx-calendar-check"></i><p>No upcoming holidays</p>
              </div>
              <div v-else class="hol-upcoming-list">
                <div v-for="h in upcomingHolidays" :key="h.id" class="hol-upcoming-item" @click="viewHolidayDetails(h)">
                  <div class="hol-upcoming-date">
                    <span class="hol-upcoming-month">{{ formatDateMonth(h.holiday_date) }}</span>
                    <span class="hol-upcoming-day">{{ formatDateDay(h.holiday_date) }}</span>
                  </div>
                  <div class="hol-upcoming-info">
                    <span class="hol-upcoming-name">{{ h.holiday_name }}</span>
                    <span class="hol-upcoming-cat">{{ h.categories || 'General' }}</span>
                  </div>
                  <span v-if="isHolidayToday(h.holiday_date)" class="hol-pill hol-pill-today">Today</span>
                  <span v-else-if="isHolidaySoon(h.holiday_date)" class="hol-pill hol-pill-soon">Soon</span>
                </div>
              </div>
            </div>
            <!-- Legend -->
            <div class="hol-card hol-legend-card">
              <div class="hol-card-header"><span class="hol-card-title"><i class="bx bx-info-circle"></i> Legend</span></div>
              <div class="hol-legend">
                <div class="hol-legend-item"><span class="hol-legend-dot" style="background:#059669"></span>Public Holiday</div>
                <div class="hol-legend-item"><span class="hol-legend-dot" style="background:#3b82f6"></span>Company Holiday</div>
                <div class="hol-legend-item"><span class="hol-legend-dot" style="background:#f59e0b"></span>Fixed / Annual</div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ══ TAB: CATEGORIES ══ -->
      <template v-if="activeTab === 'categories'">
        <div class="hol-cat-layout">
          <!-- Categories list -->
          <div class="hol-card hol-cat-main">
            <div class="hol-card-header">
              <span class="hol-card-title"><i class="bx bx-tag"></i> Holiday Categories</span>
              <button class="hol-btn hol-btn-primary hol-btn-sm" @click="openCategoryForm()"><i class="bx bx-plus"></i> Add Category</button>
            </div>
            <div v-if="catLoading" class="hol-loading"><div class="hol-spinner"></div><span>Loading...</span></div>
            <div v-else-if="!categories.length" class="hol-empty">
              <i class="bx bx-tag"></i><h5>No categories yet</h5>
              <p>Create categories to organise your holidays.</p>
              <button class="hol-btn hol-btn-primary" @click="openCategoryForm()"><i class="bx bx-plus"></i> Add Category</button>
            </div>
            <div v-else class="hol-cat-cards">
              <div v-for="cat in categories" :key="cat.id" class="hol-cat-card">
                <div class="hol-cat-card-accent" :style="{ background: cat.color_code }"></div>
                <div class="hol-cat-card-body">
                  <div class="hol-cat-card-top">
                    <div class="hol-cat-card-icon" :style="{ background: cat.color_code + '22', color: cat.color_code }">
                      <i class="bx bx-tag"></i>
                    </div>
                    <div class="hol-cat-card-info">
                      <span class="hol-cat-card-name">{{ cat.category_name }}</span>
                      <span class="hol-cat-card-desc">{{ truncateText(cat.description, 60) || 'No description' }}</span>
                    </div>
                    <div class="hol-cat-card-count" :style="{ background: cat.color_code + '22', color: cat.color_code }">
                      {{ getCategoryUsageCount(cat.id) }}
                      <span>holidays</span>
                    </div>
                  </div>
                  <div class="hol-cat-card-bar">
                    <div class="hol-cat-bar-fill" :style="{ width: getCategoryPct(cat.id) + '%', background: cat.color_code }"></div>
                  </div>
                  <div class="hol-cat-card-actions">
                    <button class="hol-action-btn hol-action-edit" @click="openCategoryForm(cat)" title="Edit"><i class="bx bx-edit"></i> Edit</button>
                    <button class="hol-action-btn hol-action-delete" @click="deleteCategory(cat)" :disabled="getCategoryUsageCount(cat.id) > 0" title="Delete"><i class="bx bx-trash"></i> Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Distribution sidebar -->
          <div class="hol-cat-sidebar">
            <div class="hol-card">
              <div class="hol-card-header"><span class="hol-card-title"><i class="bx bx-pie-chart-alt-2"></i> Distribution</span></div>
              <div v-if="!categories.length" class="hol-empty hol-empty-sm"><i class="bx bx-pie-chart-alt-2"></i><p>No data yet</p></div>
              <div v-else class="hol-dist-list">
                <div v-for="cat in categories" :key="cat.id" class="hol-dist-item">
                  <div class="hol-dist-header">
                    <span class="hol-dist-name">{{ cat.category_name }}</span>
                    <span class="hol-dist-count">{{ getCategoryUsageCount(cat.id) }}</span>
                  </div>
                  <div class="hol-dist-bar-wrap">
                    <div class="hol-dist-bar" :style="{ width: getCategoryPct(cat.id) + '%', background: cat.color_code }"></div>
                  </div>
                  <span class="hol-dist-pct">{{ getCategoryPct(cat.id) }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ══ TAB: STATISTICS ══ -->
      <template v-if="activeTab === 'statistics'">
        <!-- Stats summary row -->
        <div class="hol-stats-summary">
          <div class="hol-stat-card" style="--c:#3b82f6">
            <div class="hol-stat-icon" style="background:#eff6ff;color:#3b82f6"><i class="bx bx-calendar-check"></i></div>
            <div><span class="hol-stat-label">Working Days</span><span class="hol-stat-value">{{ statistics.workingDays || 0 }}</span><span class="hol-stat-sub">of {{ statistics.totalDays || 365 }} total</span></div>
          </div>
          <div class="hol-stat-card" style="--c:#ef4444">
            <div class="hol-stat-icon" style="background:#fef2f2;color:#ef4444"><i class="bx bx-calendar-x"></i></div>
            <div><span class="hol-stat-label">Holidays on Working Days</span><span class="hol-stat-value">{{ statistics.holidaysOnWorkingDays || 0 }}</span><span class="hol-stat-sub">days off</span></div>
          </div>
          <div class="hol-stat-card" style="--c:#059669">
            <div class="hol-stat-icon" style="background:#f0fdf4;color:#059669"><i class="bx bx-briefcase"></i></div>
            <div><span class="hol-stat-label">Net Working Days</span><span class="hol-stat-value">{{ (statistics.workingDays || 0) - (statistics.holidaysOnWorkingDays || 0) }}</span><span class="hol-stat-sub">effective days</span></div>
          </div>
          <div class="hol-stat-card" style="--c:#f59e0b">
            <div class="hol-stat-icon" style="background:#fffbeb;color:#f59e0b"><i class="bx bx-percent"></i></div>
            <div><span class="hol-stat-label">Holiday Rate</span><span class="hol-stat-value">{{ calcHolPct() }}%</span><span class="hol-stat-sub">of working days</span></div>
          </div>
        </div>

        <div class="hol-stats-layout">
          <!-- Monthly breakdown table -->
          <div class="hol-card hol-stats-main">
            <div class="hol-card-header">
              <span class="hol-card-title"><i class="bx bx-table"></i> Monthly Breakdown {{ filters.year }}</span>
              <button class="hol-btn hol-btn-ghost hol-btn-sm" @click="loadStatistics"><i class="bx bx-refresh"></i> Refresh</button>
            </div>
            <div v-if="statsLoading" class="hol-loading"><div class="hol-spinner"></div><span>Loading statistics...</span></div>
            <div v-else class="hol-table-wrap">
              <table class="hol-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th class="hol-td-center">Working Days</th>
                    <th class="hol-td-center">Holidays</th>
                    <th class="hol-td-center">Net Days</th>
                    <th class="hol-td-center">Rate</th>
                    <th>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(m, i) in months" :key="i" :class="{ 'hol-row-highlight': getMonthHols(i) === maxMonthHols && maxMonthHols > 0 }">
                    <td><span class="hol-month-name">{{ m }}</span></td>
                    <td class="hol-td-center">{{ getMonthWorkDays(i) }}</td>
                    <td class="hol-td-center">
                      <span v-if="getMonthHols(i) > 0" class="hol-pill hol-pill-hols">{{ getMonthHols(i) }}</span>
                      <span v-else class="hol-td-zero">—</span>
                    </td>
                    <td class="hol-td-center">{{ getMonthWorkDays(i) - getMonthHols(i) }}</td>
                    <td class="hol-td-center">
                      <span class="hol-rate-badge" :class="getMonthHols(i) > 0 ? 'hol-rate-has' : 'hol-rate-none'">
                        {{ getMonthPct(i) }}%
                      </span>
                    </td>
                    <td>
                      <div class="hol-month-bar-wrap">
                        <div class="hol-month-bar" :style="{ width: getMonthBarWidth(i) + '%' }"></div>
                      </div>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="hol-tfoot">
                    <td><strong>Total</strong></td>
                    <td class="hol-td-center"><strong>{{ statistics.workingDays || 0 }}</strong></td>
                    <td class="hol-td-center"><strong>{{ statistics.holidaysOnWorkingDays || 0 }}</strong></td>
                    <td class="hol-td-center"><strong>{{ (statistics.workingDays || 0) - (statistics.holidaysOnWorkingDays || 0) }}</strong></td>
                    <td class="hol-td-center"><strong>{{ calcHolPct() }}%</strong></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Sidebar: working days config + policy -->
          <div class="hol-stats-sidebar">
            <div class="hol-card">
              <div class="hol-card-header"><span class="hol-card-title"><i class="bx bx-cog"></i> Working Days</span></div>
              <div class="hol-workdays-grid">
                <label v-for="day in weekdays" :key="day" class="hol-workday-check" :class="{ 'hol-workday-on': workingDaysConfig.includes(day) }">
                  <input type="checkbox" :value="day" v-model="workingDaysConfig" hidden>
                  <span>{{ day.slice(0,3) }}</span>
                </label>
              </div>
              <div class="hol-workdays-footer">
                <button class="hol-btn hol-btn-primary hol-btn-sm" @click="saveWorkingDays" :disabled="configSaving">
                  <span v-if="configSaving" class="hol-btn-spinner"></span>
                  <i v-else class="bx bx-save"></i>
                  {{ configSaving ? 'Saving...' : 'Save Config' }}
                </button>
              </div>
            </div>
            <div class="hol-card">
              <div class="hol-card-header"><span class="hol-card-title"><i class="bx bx-info-circle"></i> Policy Summary</span></div>
              <div class="hol-policy-list">
                <div class="hol-policy-item">
                  <span class="hol-policy-label">Total Days {{ filters.year }}</span>
                  <span class="hol-policy-value">{{ statistics.totalDays || 365 }}</span>
                </div>
                <div class="hol-policy-item">
                  <span class="hol-policy-label">Working Days</span>
                  <span class="hol-policy-value hol-policy-blue">{{ statistics.workingDays || 0 }}</span>
                </div>
                <div class="hol-policy-item">
                  <span class="hol-policy-label">Holidays on Working Days</span>
                  <span class="hol-policy-value hol-policy-red">{{ statistics.holidaysOnWorkingDays || 0 }}</span>
                </div>
                <div class="hol-policy-item hol-policy-total">
                  <span class="hol-policy-label">Net Working Days</span>
                  <span class="hol-policy-value hol-policy-green">{{ (statistics.workingDays || 0) - (statistics.holidaysOnWorkingDays || 0) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

    </div>

    <!-- ══ SLIDE-IN FORM PANEL ══ -->
    <Teleport to="body">
      <div class="hol-overlay" v-if="showForm" @click.self="closeForm"></div>
      <div class="hol-panel" :class="{ 'hol-panel-open': showForm }">
        <div class="hol-panel-header">
          <div class="hol-panel-title-wrap">
            <div class="hol-panel-icon"><i class="bx" :class="isEditMode ? 'bx-edit' : 'bx-plus-circle'"></i></div>
            <div>
              <h4 class="hol-panel-title">{{ isEditMode ? 'Edit Holiday' : 'Add New Holiday' }}</h4>
              <p class="hol-panel-subtitle">{{ isEditMode ? 'Update holiday details' : 'Fill in the details below' }}</p>
            </div>
          </div>
          <button class="hol-panel-close" @click="closeForm"><i class="bx bx-x"></i></button>
        </div>
        <div class="hol-panel-body">
          <form @submit.prevent="submitHoliday">

            <div class="hol-form-section">
              <div class="hol-form-section-label"><i class="bx bx-info-circle"></i> Basic Details</div>
              <div class="hol-form-grid">
                <div class="hol-field hol-field-full">
                  <label class="hol-label">Holiday Name <span class="hol-req">*</span></label>
                  <input v-model="holidayForm.holidayName" class="hol-input" placeholder="e.g. New Year's Day" required>
                </div>
                <div class="hol-field">
                  <label class="hol-label">Date <span class="hol-req">*</span></label>
                  <div class="hol-input-icon-wrap"><i class="bx bx-calendar hol-input-icon"></i>
                    <input v-model="holidayForm.holidayDate" type="date" class="hol-input hol-input-icon-pad" required>
                  </div>
                </div>
                <div class="hol-field">
                  <label class="hol-label">Country</label>
                  <select v-model="holidayForm.countryCode" class="hol-select">
                    <option value="KE">Kenya (KE)</option>
                    <option value="US">United States (US)</option>
                    <option value="GB">United Kingdom (GB)</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div class="hol-field hol-field-full">
                  <label class="hol-label">Description</label>
                  <textarea v-model="holidayForm.description" class="hol-textarea" rows="3" placeholder="Optional description..."></textarea>
                </div>
              </div>
            </div>

            <div class="hol-form-section">
              <div class="hol-form-section-label"><i class="bx bx-cog"></i> Settings</div>
              <div class="hol-toggle-row">
                <label class="hol-toggle-item" :class="{ 'hol-toggle-on': holidayForm.isPublic }">
                  <div class="hol-toggle-info"><i class="bx bx-world"></i><div><span class="hol-toggle-title">Public Holiday</span><span class="hol-toggle-desc">Visible to all employees</span></div></div>
                  <div class="hol-switch" @click="holidayForm.isPublic = !holidayForm.isPublic"><div class="hol-switch-knob"></div></div>
                </label>
                <label class="hol-toggle-item" :class="{ 'hol-toggle-on': holidayForm.isFixed }">
                  <div class="hol-toggle-info"><i class="bx bx-repeat"></i><div><span class="hol-toggle-title">Fixed Holiday</span><span class="hol-toggle-desc">Recurs every year</span></div></div>
                  <div class="hol-switch" @click="holidayForm.isFixed = !holidayForm.isFixed"><div class="hol-switch-knob"></div></div>
                </label>
              </div>
            </div>

            <div class="hol-form-section">
              <div class="hol-form-section-label"><i class="bx bx-tag"></i> Categories</div>
              <div class="hol-cat-checks">
                <label v-for="cat in categories" :key="cat.id" class="hol-cat-check" :class="{ 'hol-cat-checked': holidayForm.categories.includes(cat.id) }">
                  <input type="checkbox" :value="cat.id" v-model="holidayForm.categories" hidden>
                  <span class="hol-cat-dot" :style="{ background: cat.color_code }"></span>
                  {{ cat.category_name }}
                </label>
              </div>
            </div>

            <div class="hol-form-section">
              <div class="hol-form-section-label"><i class="bx bx-buildings"></i> Department Scope</div>
              <label class="hol-toggle-item" :class="{ 'hol-toggle-on': holidayForm.specifyDepartments }">
                <div class="hol-toggle-info"><i class="bx bx-filter"></i><div><span class="hol-toggle-title">Specific Departments</span><span class="hol-toggle-desc">Limit to selected departments</span></div></div>
                <div class="hol-switch" @click="holidayForm.specifyDepartments = !holidayForm.specifyDepartments"><div class="hol-switch-knob"></div></div>
              </label>
              <div v-if="holidayForm.specifyDepartments" class="hol-dept-grid">
                <label v-for="dept in departments" :key="dept.id" class="hol-dept-check" :class="{ 'hol-dept-checked': holidayForm.departmentIds.includes(dept.id) }">
                  <input type="checkbox" :value="dept.id" v-model="holidayForm.departmentIds" hidden>
                  <i class="bx" :class="holidayForm.departmentIds.includes(dept.id) ? 'bx-check-square' : 'bx-square'"></i>
                  {{ dept.departmentName }}
                </label>
              </div>
              <label v-if="holidayForm.specifyDepartments" class="hol-toggle-item hol-mt-2" :class="{ 'hol-toggle-on': holidayForm.halfDay }">
                <div class="hol-toggle-info"><i class="bx bx-time-five"></i><div><span class="hol-toggle-title">Half-Day Holiday</span><span class="hol-toggle-desc">Only half the working day off</span></div></div>
                <div class="hol-switch" @click="holidayForm.halfDay = !holidayForm.halfDay"><div class="hol-switch-knob"></div></div>
              </label>
            </div>

            <div class="hol-panel-footer">
              <button type="button" class="hol-btn hol-btn-ghost" @click="closeForm"><i class="bx bx-x"></i> Cancel</button>
              <button type="submit" class="hol-btn hol-btn-primary" :disabled="submitLoading">
                <span v-if="submitLoading" class="hol-btn-spinner"></span>
                <i v-else class="bx" :class="isEditMode ? 'bx-save' : 'bx-check'"></i>
                {{ submitLoading ? 'Saving...' : (isEditMode ? 'Update Holiday' : 'Save Holiday') }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Category Form Panel -->
      <div class="hol-overlay" v-if="showCatForm" @click.self="closeCategoryForm"></div>
      <div class="hol-panel hol-panel-sm" :class="{ 'hol-panel-open': showCatForm }">
        <div class="hol-panel-header" style="background: linear-gradient(135deg,#7c3aed,#6d28d9)">
          <div class="hol-panel-title-wrap">
            <div class="hol-panel-icon"><i class="bx bx-tag"></i></div>
            <div>
              <h4 class="hol-panel-title">{{ isCatEditMode ? 'Edit Category' : 'Add Category' }}</h4>
              <p class="hol-panel-subtitle">Organise holidays by type</p>
            </div>
          </div>
          <button class="hol-panel-close" @click="closeCategoryForm"><i class="bx bx-x"></i></button>
        </div>
        <div class="hol-panel-body">
          <form @submit.prevent="submitCategory">
            <div class="hol-form-section">
              <div class="hol-form-section-label"><i class="bx bx-info-circle"></i> Category Details</div>
              <div class="hol-form-grid">
                <div class="hol-field hol-field-full">
                  <label class="hol-label">Name <span class="hol-req">*</span></label>
                  <input v-model="categoryForm.categoryName" class="hol-input" placeholder="e.g. Public Holiday" required>
                </div>
                <div class="hol-field hol-field-full">
                  <label class="hol-label">Description</label>
                  <textarea v-model="categoryForm.description" class="hol-textarea" rows="2" placeholder="Optional..."></textarea>
                </div>
                <div class="hol-field hol-field-full">
                  <label class="hol-label">Color</label>
                  <div class="hol-color-row">
                    <input type="color" v-model="categoryForm.colorCode" class="hol-color-input">
                    <div class="hol-color-preview" :style="{ background: categoryForm.colorCode }">
                      <span>{{ categoryForm.categoryName || 'Preview' }}</span>
                    </div>
                  </div>
                  <div class="hol-quick-colors">
                    <button v-for="c in quickColors" :key="c" type="button" class="hol-quick-color" :style="{ background: c }" @click="categoryForm.colorCode = c"></button>
                  </div>
                </div>
              </div>
            </div>
            <div class="hol-panel-footer">
              <button type="button" class="hol-btn hol-btn-ghost" @click="closeCategoryForm"><i class="bx bx-x"></i> Cancel</button>
              <button type="submit" class="hol-btn hol-btn-primary" :disabled="catSubmitLoading">
                <span v-if="catSubmitLoading" class="hol-btn-spinner"></span>
                <i v-else class="bx bx-check"></i>
                {{ catSubmitLoading ? 'Saving...' : (isCatEditMode ? 'Update' : 'Save Category') }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- View Details Panel -->
      <div class="hol-overlay" v-if="showDetails" @click.self="showDetails = false"></div>
      <div class="hol-panel" :class="{ 'hol-panel-open': showDetails }">
        <div class="hol-panel-header" v-if="selectedHoliday" :style="{ background: 'linear-gradient(135deg,#1e293b,#334155)' }">
          <div class="hol-panel-title-wrap">
            <div class="hol-panel-icon"><i class="bx bx-calendar-event"></i></div>
            <div>
              <h4 class="hol-panel-title">{{ selectedHoliday.holiday_name }}</h4>
              <p class="hol-panel-subtitle">{{ formatDate(selectedHoliday.holiday_date) }}</p>
            </div>
          </div>
          <button class="hol-panel-close" @click="showDetails = false"><i class="bx bx-x"></i></button>
        </div>
        <div class="hol-panel-body" v-if="selectedHoliday">
          <div class="hol-detail-meta">
            <div class="hol-detail-meta-item"><i class="bx bx-calendar"></i><div><span class="hol-meta-label">Date</span><span class="hol-meta-value">{{ formatDate(selectedHoliday.holiday_date) }}</span></div></div>
            <div class="hol-detail-meta-item"><i class="bx bx-world"></i><div><span class="hol-meta-label">Country</span><span class="hol-meta-value">{{ selectedHoliday.country_code }}</span></div></div>
            <div class="hol-detail-meta-item"><i class="bx bx-badge-check"></i><div><span class="hol-meta-label">Type</span><span class="hol-meta-value">{{ selectedHoliday.is_public == 1 ? 'Public' : 'Non-Public' }}</span></div></div>
            <div class="hol-detail-meta-item"><i class="bx bx-repeat"></i><div><span class="hol-meta-label">Recurrence</span><span class="hol-meta-value">{{ selectedHoliday.is_fixed == 1 ? 'Annual' : 'One-time' }}</span></div></div>
          </div>
          <div class="hol-form-section" v-if="selectedHoliday.description">
            <div class="hol-form-section-label"><i class="bx bx-file-blank"></i> Description</div>
            <p class="hol-detail-desc">{{ selectedHoliday.description }}</p>
          </div>
          <div class="hol-form-section" v-if="holidayCategories && holidayCategories.length">
            <div class="hol-form-section-label"><i class="bx bx-tag"></i> Categories</div>
            <div class="hol-cat-tags">
              <span v-for="cat in holidayCategories" :key="cat.id" class="hol-cat-tag" :style="{ background: cat.color_code }">{{ cat.category_name }}</span>
            </div>
          </div>
          <div class="hol-form-section" v-if="holidayObservations && holidayObservations.length">
            <div class="hol-form-section-label"><i class="bx bx-buildings"></i> Department Observations</div>
            <table class="hol-table">
              <thead><tr><th>Department</th><th>Location</th><th>Half Day</th></tr></thead>
              <tbody>
                <tr v-for="(obs, i) in holidayObservations" :key="i">
                  <td>{{ getDepartmentName(obs.department_id) || 'All' }}</td>
                  <td>{{ getLocationName(obs.location_id) || 'All' }}</td>
                  <td>{{ obs.half_day == 1 ? 'Yes' : 'No' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="hol-panel-footer">
            <button class="hol-btn hol-btn-ghost" @click="showDetails = false"><i class="bx bx-x"></i> Close</button>
            <button class="hol-btn hol-btn-primary" @click="showDetails = false; editHoliday(selectedHoliday)"><i class="bx bx-edit"></i> Edit</button>
          </div>
        </div>
      </div>
    </Teleport>

  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

export default {
  name: "Holidays",
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    return {
      activeTab: 'list',
      loading: false,
      catLoading: false,
      submitLoading: false,
      catSubmitLoading: false,
      holidays: [],
      categories: [],
      departments: [],
      locations: [],
      metrics: { totalHolidays: 0, publicHolidays: 0, fixedHolidays: 0, holidayMonths: 0 },
      filters: { year: y, month: '', categoryId: '', isPublic: '', search: '' },
      months: ['January','February','March','April','May','June','July','August','September','October','November','December'],
      // Holiday form
      showForm: false,
      isEditMode: false,
      selectedHoliday: null,
      holidayCategories: [],
      holidayObservations: [],
      holidayForm: { holidayName:'', holidayDate:'', description:'', countryCode:'KE', isPublic:true, isFixed:false, categories:[], specifyDepartments:false, departmentIds:[], locationIds:[], halfDay:false },
      // Details panel
      showDetails: false,
      // Category form
      showCatForm: false,
      isCatEditMode: false,
      categoryForm: { categoryName:'', description:'', colorCode:'#059669' },
      categoryUsage: [],
      quickColors: ['#ef4444','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#0ea5e9','#64748b'],
      // Calendar
      calendar: null,
      calendarView: 'month',
      upcomingHolidays: [],
      // Statistics
      statsLoading: false,
      statistics: { totalDays:0, workingDays:0, holidaysOnWorkingDays:0, monthlyWorkingDays:[], monthlyHolidaysOnWorkingDays:[], workingDaysList:[] },
      weekdays: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
      workingDaysConfig: ['Monday','Tuesday','Wednesday','Thursday','Friday'],
      configSaving: false
    };
  },
  computed: {
    yearOptions() {
      const y = new Date().getFullYear();
      return [y-2, y-1, y, y+1, y+2];
    },
    maxMonthHols() {
      return Math.max(...(this.statistics.monthlyHolidaysOnWorkingDays || [0]));
    }
  },
  mounted() {
    this.getDepartments();
    this.getLocations();
    this.getHolidayCategories();
    this.getHolidays();
  },
  methods: {
    // ── Data Loading ──
    getHolidays() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getHolidays");
      data.append("year", this.filters.year);
      if (this.filters.month) data.append("month", this.filters.month);
      if (this.filters.categoryId) data.append("categoryId", this.filters.categoryId);
      if (this.filters.isPublic !== '') data.append("isPublic", this.filters.isPublic);
      if (this.filters.search) data.append("search", this.filters.search);
      execute(data, authUrl).then(res => {
        this.loading = false;
        if (res.data.success) {
          this.holidays = res.data.data.holidays || [];
          this.metrics = res.data.data.metrics || {};
          if (res.data.data.categories) this.categories = res.data.data.categories;
        } else {
          toast.error(res.data.message || "Failed to load holidays");
          this.holidays = [];
        }
      }).catch(() => { this.loading = false; toast.error("Network error"); });
    },
    getDepartments() {
      const data = new FormData(); data.append("function", "getDepartments");
      execute(data, authUrl).then(res => { if (res.data.success) this.departments = res.data.data.departments || []; });
    },
    getLocations() {
      const data = new FormData(); data.append("function", "getLocations");
      execute(data, authUrl).then(res => { if (res.data.success) this.locations = res.data.data.locations || []; });
    },
    getHolidayCategories() {
      this.catLoading = true;
      const data = new FormData(); data.append("function", "getHolidayCategories");
      execute(data, authUrl).then(res => {
        this.catLoading = false;
        if (res.data.success) {
          this.categories = res.data.data.categories || [];
          this.loadCategoryUsage();
        }
      }).catch(() => { this.catLoading = false; });
    },
    loadCategoryUsage() {
      const data = new FormData();
      data.append("function", "getHolidayStatistics");
      data.append("year", this.filters.year);
      execute(data, authUrl).then(res => {
        if (res.data.success && res.data.data.categoryStats) {
          this.categoryUsage = res.data.data.categoryStats;
        }
      });
    },
    refreshData() { this.getHolidays(); },
    resetFilters() {
      this.filters = { year: new Date().getFullYear(), month:'', categoryId:'', isPublic:'', search:'' };
      this.getHolidays();
    },
    handleSearch: _.debounce(function() { this.getHolidays(); }, 500),

    // ── Holiday Form ──
    openForm(holiday) {
      this.isEditMode = !!holiday;
      if (holiday) {
        this.selectedHoliday = holiday;
        this.getHolidayDetails(holiday.id, () => { this.prepareEditForm(); this.showForm = true; });
      } else {
        this.holidayForm = { holidayName:'', holidayDate: new Date().toISOString().split('T')[0], description:'', countryCode:'KE', isPublic:true, isFixed:false, categories:[], specifyDepartments:false, departmentIds:[], locationIds:[], halfDay:false };
        this.showForm = true;
      }
    },
    closeForm() { this.showForm = false; },
    editHoliday(holiday) { this.showDetails = false; this.openForm(holiday); },
    prepareEditForm() {
      if (!this.selectedHoliday) return;
      let categoryIds = [];
      if (this.selectedHoliday.category_ids) {
        categoryIds = Array.isArray(this.selectedHoliday.category_ids)
          ? this.selectedHoliday.category_ids
          : this.selectedHoliday.category_ids.split(',').map(id => parseInt(id.trim()));
      }
      const hasObs = this.holidayObservations && this.holidayObservations.length > 0;
      let departmentIds = [], locationIds = [], halfDay = false;
      if (hasObs) {
        halfDay = this.holidayObservations[0].half_day == 1;
        departmentIds = this.holidayObservations.filter(o => o.department_id).map(o => o.department_id);
        locationIds = this.holidayObservations.filter(o => o.location_id).map(o => o.location_id);
      }
      this.holidayForm = {
        holidayId: this.selectedHoliday.id,
        holidayName: this.selectedHoliday.holiday_name,
        holidayDate: this.selectedHoliday.holiday_date,
        description: this.selectedHoliday.description || '',
        countryCode: this.selectedHoliday.country_code || 'KE',
        isPublic: this.selectedHoliday.is_public == 1,
        isFixed: this.selectedHoliday.is_fixed == 1,
        categories: categoryIds,
        specifyDepartments: hasObs,
        departmentIds, locationIds, halfDay
      };
    },
    submitHoliday() {
      if (!this.holidayForm.holidayName.trim()) { toast.error("Holiday name is required"); return; }
      if (!this.holidayForm.holidayDate) { toast.error("Holiday date is required"); return; }
      this.submitLoading = true;
      const data = new FormData();
      data.append("function", this.isEditMode ? "updateHoliday" : "createHoliday");
      if (this.isEditMode) data.append("holidayId", this.holidayForm.holidayId);
      data.append("holidayName", this.holidayForm.holidayName);
      data.append("holidayDate", this.holidayForm.holidayDate);
      data.append("description", this.holidayForm.description);
      data.append("countryCode", this.holidayForm.countryCode);
      data.append("isPublic", this.holidayForm.isPublic ? 1 : 0);
      data.append("isFixed", this.holidayForm.isFixed ? 1 : 0);
      if (this.holidayForm.categories.length) data.append("categories", JSON.stringify(this.holidayForm.categories));
      if (this.holidayForm.specifyDepartments) {
        if (this.holidayForm.departmentIds.length) data.append("departmentIds", JSON.stringify(this.holidayForm.departmentIds));
        if (this.holidayForm.locationIds.length) data.append("locationIds", JSON.stringify(this.holidayForm.locationIds));
        data.append("halfDay", this.holidayForm.halfDay ? 1 : 0);
      }
      execute(data, authUrl).then(res => {
        this.submitLoading = false;
        if (res.data.success) { this.closeForm(); toast.success(res.data.message); this.getHolidays(); }
        else toast.error(res.data.message || "Failed to save holiday");
      }).catch(() => { this.submitLoading = false; toast.error("Network error"); });
    },
    deleteHoliday(holiday) {
      if (!confirm(`Delete "${holiday.holiday_name}"?`)) return;
      const data = new FormData();
      data.append("function", "deleteHoliday");
      data.append("holidayId", holiday.id);
      execute(data, authUrl).then(res => {
        if (res.data.success) { toast.success(res.data.message); this.getHolidays(); }
        else toast.error(res.data.message);
      });
    },
    viewHolidayDetails(holiday) {
      this.getHolidayDetails(holiday.id, () => { this.showDetails = true; });
    },
    getHolidayDetails(holidayId, callback) {
      const data = new FormData();
      data.append("function", "getHolidayDetails");
      data.append("holidayId", holidayId);
      execute(data, authUrl).then(res => {
        if (res.data.success) {
          this.selectedHoliday = res.data.data.holiday;
          this.holidayCategories = res.data.data.categories || [];
          this.holidayObservations = res.data.data.observations || [];
          if (callback) callback();
        } else toast.error(res.data.message);
      });
    },
    exportHolidays() {
      window.open(`${authUrl}?function=exportHolidays&year=${this.filters.year}`, '_blank');
    },

    // ── Category Form ──
    openCategoryForm(cat) {
      this.isCatEditMode = !!cat;
      this.categoryForm = cat
        ? { categoryId: cat.id, categoryName: cat.category_name, description: cat.description || '', colorCode: cat.color_code || '#059669' }
        : { categoryName:'', description:'', colorCode:'#059669' };
      this.showCatForm = true;
    },
    closeCategoryForm() { this.showCatForm = false; },
    submitCategory() {
      if (!this.categoryForm.categoryName.trim()) { toast.error("Category name is required"); return; }
      this.catSubmitLoading = true;
      const data = new FormData();
      data.append("function", this.isCatEditMode ? "updateHolidayCategory" : "createHolidayCategory");
      if (this.isCatEditMode) data.append("categoryId", this.categoryForm.categoryId);
      data.append("categoryName", this.categoryForm.categoryName);
      data.append("description", this.categoryForm.description);
      data.append("colorCode", this.categoryForm.colorCode);
      execute(data, authUrl).then(res => {
        this.catSubmitLoading = false;
        if (res.data.success) { this.closeCategoryForm(); toast.success(res.data.message); this.getHolidayCategories(); }
        else toast.error(res.data.message || "Failed to save category");
      }).catch(() => { this.catSubmitLoading = false; toast.error("Network error"); });
    },
    deleteCategory(cat) {
      if (this.getCategoryUsageCount(cat.id) > 0) { toast.error("Cannot delete a category in use"); return; }
      if (!confirm(`Delete category "${cat.category_name}"?`)) return;
      const data = new FormData();
      data.append("function", "deleteHolidayCategory");
      data.append("categoryId", cat.id);
      execute(data, authUrl).then(res => {
        if (res.data.success) { toast.success(res.data.message); this.getHolidayCategories(); }
        else toast.error(res.data.message);
      });
    },
    getCategoryUsageCount(catId) {
      const s = this.categoryUsage.find(c => c.id == catId);
      return s ? s.count : 0;
    },

    // ── Calendar ──
    initCalendar() {
      this.$nextTick(() => {
        const el = document.getElementById('holidays-calendar');
        if (!el || this.calendar) return;
        this.calendar = new Calendar(el, {
          plugins: [dayGridPlugin, interactionPlugin],
          initialView: 'dayGridMonth',
          headerToolbar: { left:'prev,next today', center:'title', right:'' },
          events: [],
          eventClick: info => {
            const id = info.event.id;
            if (id) this.getHolidayDetails(id, () => { this.showDetails = true; });
          },
          height: 'auto'
        });
        this.calendar.render();
        this.updateCalendarEvents();
      });
    },
    setCalView(view) {
      this.calendarView = view;
      if (this.calendar) {
        this.calendar.changeView(view === 'year' ? 'dayGridYear' : 'dayGridMonth');
      }
    },
    updateCalendarEvents() {
      if (!this.calendar) return;
      const data = new FormData();
      data.append("function", "getHolidayCalendar");
      data.append("year", this.filters.year);
      execute(data, authUrl).then(res => {
        if (res.data.success && this.calendar) {
          this.calendar.removeAllEvents();
          this.calendar.addEventSource(res.data.data.events || []);
          this.upcomingHolidays = res.data.data.upcomingHolidays || [];
          this.calendar.gotoDate(`${this.filters.year}-01-01`);
        }
      });
    },

    // ── Statistics ──
    loadStatistics() {
      this.statsLoading = true;
      const data = new FormData();
      data.append("function", "getHolidayStatistics");
      data.append("year", this.filters.year);
      execute(data, authUrl).then(res => {
        this.statsLoading = false;
        if (res.data.success) {
          this.statistics = res.data.data;
          this.workingDaysConfig = res.data.data.workingDaysList || ['Monday','Tuesday','Wednesday','Thursday','Friday'];
        }
      }).catch(() => { this.statsLoading = false; });
    },
    saveWorkingDays() {
      if (!this.workingDaysConfig.length) { toast.error("Select at least one working day"); return; }
      this.configSaving = true;
      const data = new FormData();
      data.append("function", "updateHolidayConfigurations");
      data.append("settings[working_days]", this.workingDaysConfig.join(','));
      execute(data, authUrl).then(res => {
        this.configSaving = false;
        if (res.data.success) { toast.success("Working days saved"); this.loadStatistics(); }
        else toast.error(res.data.message || "Failed to save");
      }).catch(() => { this.configSaving = false; toast.error("Network error"); });
    },
    calcHolPct() {
      const w = this.statistics.workingDays || 0;
      const h = this.statistics.holidaysOnWorkingDays || 0;
      return w > 0 ? ((h / w) * 100).toFixed(1) : '0.0';
    },
    getMonthWorkDays(i) { return (this.statistics.monthlyWorkingDays || [])[i] || 0; },
    getMonthHols(i) { return (this.statistics.monthlyHolidaysOnWorkingDays || [])[i] || 0; },
    getMonthPct(i) {
      const w = this.getMonthWorkDays(i), h = this.getMonthHols(i);
      return w > 0 ? ((h / w) * 100).toFixed(1) : '0.0';
    },
    getMonthBarWidth(i) {
      const max = Math.max(...(this.statistics.monthlyHolidaysOnWorkingDays || [1]));
      return max > 0 ? Math.round((this.getMonthHols(i) / max) * 100) : 0;
    },
    // ── Category helpers ──
    getCategoryPct(catId) {
      const total = this.categories.reduce((s, c) => s + this.getCategoryUsageCount(c.id), 0);
      return total > 0 ? Math.round((this.getCategoryUsageCount(catId) / total) * 100) : 0;
    },
    formatDateMonth(d) { if (!d) return ''; return new Date(d).toLocaleDateString('en-GB', { month:'short' }); },
    formatDateDay(d) { if (!d) return ''; return new Date(d).getDate(); },

    // ── Utilities ──
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
    },
    isHolidayToday(d) {
      if (!d) return false;
      const t = new Date(), h = new Date(d);
      return t.getFullYear()===h.getFullYear() && t.getMonth()===h.getMonth() && t.getDate()===h.getDate();
    },
    isHolidaySoon(d) {
      if (!d) return false;
      const diff = (new Date(d) - new Date()) / 86400000;
      return diff > 0 && diff <= 7;
    },
    getCategoryList(cats) { return cats ? cats.split(',') : []; },
    getCategoryColor(colors, idx) {
      if (!colors) return '#3b82f6';
      const arr = colors.split(',');
      return arr[idx] || arr[0] || '#3b82f6';
    },
    getCategoryFirstColor(colors) {
      if (!colors) return '#3b82f6';
      return colors.split(',')[0] || '#3b82f6';
    },
    getDepartmentName(id) {
      const d = this.departments.find(x => x.id == id);
      return d ? d.departmentName : null;
    },
    getLocationName(id) {
      const l = this.locations.find(x => x.id == id);
      return l ? l.locationName : null;
    },
    truncateText(text, max) {
      if (!text) return '';
      return text.length <= max ? text : text.substring(0, max) + '...';
    }
  },
  watch: {
    'filters.year'() { this.getHolidays(); if (this.calendar) this.updateCalendarEvents(); }
  },
  beforeUnmount() {
    if (this.calendar) this.calendar.destroy();
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hol-page { display: flex; flex-direction: column; gap: 20px; }

/* ── Toolbar ── */
.hol-toolbar { display: flex; align-items: center; justify-content: space-between; background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; padding: 8px 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); flex-wrap: wrap; gap: 10px; }
.hol-tabs { display: flex; gap: 4px; background: #f1f5f9; border-radius: 10px; padding: 4px; }
.hol-tab { display: flex; align-items: center; gap: 7px; padding: 8px 16px; border-radius: 8px; border: none; background: transparent; color: #64748b; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.18s; white-space: nowrap; }
.hol-tab i { font-size: 16px; }
.hol-tab:hover { background: #e2e8f0; color: #1e293b; }
.hol-tab-active { background: #fff !important; color: #059669 !important; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.hol-tab-count { background: #059669; color: #fff; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 20px; }
.hol-toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hol-year-select { display: flex; align-items: center; gap: 6px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px 10px; color: #475569; font-size: 13px; }
.hol-year-select i { font-size: 15px; color: #059669; }
.hol-year-select select { border: none; background: transparent; font-size: 13px; font-weight: 600; color: #1e293b; outline: none; cursor: pointer; }
.hol-btn { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; border: 1px solid transparent; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.hol-btn i { font-size: 15px; }
.hol-btn-sm { padding: 6px 12px; font-size: 12px; }
.hol-btn-ghost { background: #f8fafc; color: #475569; border-color: #e2e8f0; }
.hol-btn-ghost:hover { background: #e2e8f0; }
.hol-btn-outline { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
.hol-btn-outline:hover { background: #16a34a; color: #fff; border-color: #16a34a; }
.hol-btn-primary { background: linear-gradient(135deg,#059669,#0d9488); color: #fff; box-shadow: 0 2px 8px rgba(5,150,105,0.3); }
.hol-btn-primary:hover:not(:disabled) { background: linear-gradient(135deg,#047857,#0f766e); transform: translateY(-1px); }
.hol-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.hol-btn-spinner { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: hol-spin 0.7s linear infinite; display: inline-block; }
@keyframes hol-spin { to { transform: rotate(360deg); } }

/* ── Summary Cards ── */
.hol-summary-row { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
.hol-summary-card { background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; padding: 18px 20px; display: flex; align-items: center; gap: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); position: relative; overflow: hidden; }
.hol-summary-card::before { content:''; position: absolute; top:0; left:0; right:0; height:3px; background: var(--accent,#059669); }
.hol-summary-icon { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; }
.hol-summary-label { display: block; font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.hol-summary-value { display: block; font-size: 28px; font-weight: 800; color: #1e293b; line-height: 1; }
.hol-summary-value small { font-size: 14px; color: #94a3b8; font-weight: 500; }

/* ── Cards ── */
.hol-card { background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
.hol-card-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
.hol-card-title { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 700; color: #1e293b; }
.hol-card-title i { font-size: 18px; color: #059669; }
.hol-row-count { font-size: 12px; color: #94a3b8; font-weight: 600; background: #f1f5f9; padding: 3px 10px; border-radius: 20px; }

/* ── Filters ── */
.hol-filters-card .hol-filters-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; padding: 16px 20px; }
.hol-filter-group { display: flex; flex-direction: column; gap: 4px; min-width: 140px; }
.hol-filter-group label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; }
.hol-filter-search { flex: 1; min-width: 200px; }
.hol-filter-reset { align-self: flex-end; }
.hol-select { padding: 7px 10px; border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: 13px; color: #1e293b; background: #fff; outline: none; cursor: pointer; }
.hol-select:focus { border-color: #059669; }
.hol-input { width: 100%; padding: 7px 10px; border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: 13px; color: #1e293b; background: #fff; outline: none; box-sizing: border-box; }
.hol-input:focus { border-color: #059669; }
.hol-search-wrap { position: relative; }
.hol-search-wrap i { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); font-size: 15px; color: #94a3b8; pointer-events: none; }
.hol-search-wrap .hol-input { padding-left: 30px; }

/* ── Table ── */
.hol-loading { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 60px 20px; color: #64748b; font-size: 14px; }
.hol-spinner { width: 22px; height: 22px; border: 3px solid #e2e8f0; border-top-color: #059669; border-radius: 50%; animation: hol-spin 0.8s linear infinite; }
.hol-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; color: #94a3b8; text-align: center; }
.hol-empty i { font-size: 52px; color: #059669; margin-bottom: 12px; }
.hol-empty h5 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0 0 6px; }
.hol-empty p { font-size: 13px; margin: 0 0 16px; }
.hol-table-wrap { overflow-x: auto; }
.hol-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hol-table th { background: #f8fafc; padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.hol-table td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
.hol-table tr:last-child td { border-bottom: none; }
.hol-table tr:hover td { background: #f8fafc; }
.hol-td-num { color: #94a3b8; font-size: 12px; font-weight: 600; width: 40px; }
.hol-name-cell { display: flex; align-items: center; gap: 10px; }
.hol-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.hol-name { font-weight: 700; color: #1e293b; display: block; }
.hol-desc { font-size: 11px; color: #94a3b8; display: block; margin-top: 2px; }
.hol-date { font-weight: 600; color: #1e293b; }
.hol-pill { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; margin-left: 4px; }
.hol-pill-public { background: #f0fdf4; color: #059669; }
.hol-pill-private { background: #f1f5f9; color: #64748b; }
.hol-pill-fixed { background: #f0f9ff; color: #0ea5e9; }
.hol-pill-today { background: #059669; color: #fff; }
.hol-pill-soon { background: #f59e0b; color: #fff; }
.hol-pill-count { background: #eff6ff; color: #2563eb; }
.hol-cat-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.hol-cat-tag { padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; color: #fff; }
.hol-no-cat { color: #94a3b8; font-size: 12px; }
.hol-row-actions { display: flex; gap: 6px; }
.hol-action-btn { width: 30px; height: 30px; border-radius: 7px; border: none; display: flex; align-items: center; justify-content: center; font-size: 15px; cursor: pointer; transition: all 0.15s; }
.hol-action-view { background: #eff6ff; color: #2563eb; }
.hol-action-view:hover { background: #2563eb; color: #fff; }
.hol-action-edit { background: #f0fdf4; color: #059669; }
.hol-action-edit:hover { background: #059669; color: #fff; }
.hol-action-delete { background: #fef2f2; color: #ef4444; }
.hol-action-delete:hover { background: #ef4444; color: #fff; }
.hol-action-delete:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Categories ── */
.hol-cat-name-cell { display: flex; align-items: center; gap: 10px; }
.hol-cat-swatch { width: 14px; height: 14px; border-radius: 50%; flex-shrink: 0; }

/* ── Calendar ── */
.hol-calendar-wrap { padding: 16px; min-height: 600px; }

/* ── Slide-in Panel ── */
.hol-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 1040; }
.hol-panel { position: fixed; top: 0; right: -520px; width: 500px; height: 100vh; background: #fff; z-index: 1050; display: flex; flex-direction: column; transition: right 0.3s cubic-bezier(0.4,0,0.2,1); box-shadow: -8px 0 40px rgba(0,0,0,0.15); }
.hol-panel-sm { width: 420px; right: -440px; }
.hol-panel-open { right: 0 !important; }
.hol-panel-header { background: linear-gradient(135deg,#059669,#0d9488); padding: 20px 24px; display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-shrink: 0; }
.hol-panel-title-wrap { display: flex; align-items: center; gap: 14px; }
.hol-panel-icon { width: 46px; height: 46px; border-radius: 12px; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-size: 22px; color: #fff; flex-shrink: 0; }
.hol-panel-title { margin: 0; font-size: 17px; font-weight: 700; color: #fff; }
.hol-panel-subtitle { margin: 3px 0 0; font-size: 12px; color: rgba(255,255,255,0.75); }
.hol-panel-close { background: rgba(255,255,255,0.15); border: none; color: #fff; width: 34px; height: 34px; border-radius: 8px; font-size: 20px; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: background 0.15s; }
.hol-panel-close:hover { background: rgba(255,255,255,0.3); }
.hol-panel-body { flex: 1; overflow-y: auto; padding: 0; }
.hol-panel-footer { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; background: #fff; border-top: 1px solid #e2e8f0; flex-shrink: 0; gap: 10px; }

/* ── Form Sections ── */
.hol-form-section { border-bottom: 1px solid #f1f5f9; }
.hol-form-section:last-child { border-bottom: none; }
.hol-form-section-label { display: flex; align-items: center; gap: 8px; padding: 12px 20px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; }
.hol-form-section-label i { font-size: 15px; color: #059669; }
.hol-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; padding: 16px 20px; }
.hol-field { display: flex; flex-direction: column; gap: 5px; }
.hol-field-full { grid-column: span 2; }
.hol-label { font-size: 12px; font-weight: 700; color: #374151; }
.hol-req { color: #ef4444; }
.hol-input-icon-wrap { position: relative; }
.hol-input-icon { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); font-size: 15px; color: #94a3b8; pointer-events: none; }
.hol-input-icon-pad { padding-left: 30px; }
.hol-textarea { width: 100%; padding: 8px 12px; border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: 13px; color: #1e293b; background: #fff; outline: none; resize: vertical; box-sizing: border-box; }
.hol-textarea:focus { border-color: #059669; }

/* Toggles */
.hol-toggle-row { display: flex; flex-direction: column; gap: 10px; padding: 14px 20px; }
.hol-toggle-item { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; cursor: pointer; transition: all 0.15s; background: #f8fafc; }
.hol-toggle-item:hover { border-color: #059669; }
.hol-toggle-on { border-color: #059669 !important; background: #f0fdf4 !important; }
.hol-toggle-info { display: flex; align-items: center; gap: 10px; }
.hol-toggle-info i { font-size: 20px; color: #94a3b8; }
.hol-toggle-on .hol-toggle-info i { color: #059669; }
.hol-toggle-title { display: block; font-size: 13px; font-weight: 700; color: #1e293b; }
.hol-toggle-desc { display: block; font-size: 11px; color: #94a3b8; }
.hol-switch { width: 42px; height: 22px; border-radius: 11px; background: #e2e8f0; position: relative; transition: background 0.2s; flex-shrink: 0; }
.hol-toggle-on .hol-switch { background: #059669; }
.hol-switch-knob { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.2); }
.hol-toggle-on .hol-switch-knob { transform: translateX(20px); }
.hol-mt-2 { margin-top: 10px; }

/* Category checks */
.hol-cat-checks { display: flex; flex-wrap: wrap; gap: 8px; padding: 14px 20px; }
.hol-cat-check { display: flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 7px; border: 1.5px solid #e2e8f0; font-size: 12px; font-weight: 600; color: #475569; cursor: pointer; background: #f8fafc; transition: all 0.15s; }
.hol-cat-check:hover { border-color: #059669; }
.hol-cat-checked { border-color: #059669 !important; background: #f0fdf4 !important; color: #059669 !important; }
.hol-cat-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }

/* Dept checks */
.hol-dept-grid { display: flex; flex-wrap: wrap; gap: 8px; padding: 10px 20px 14px; }
.hol-dept-check { display: flex; align-items: center; gap: 6px; padding: 5px 10px; border-radius: 7px; border: 1.5px solid #e2e8f0; font-size: 12px; font-weight: 600; color: #475569; cursor: pointer; background: #f8fafc; transition: all 0.15s; }
.hol-dept-check:hover { border-color: #059669; }
.hol-dept-checked { border-color: #059669 !important; background: #f0fdf4 !important; color: #059669 !important; }
.hol-dept-check i { font-size: 14px; }

/* Color picker */
.hol-color-row { display: flex; gap: 10px; align-items: center; }
.hol-color-input { width: 44px; height: 44px; border: none; border-radius: 8px; cursor: pointer; padding: 2px; }
.hol-color-preview { flex: 1; height: 44px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.hol-color-preview span { color: #fff; font-size: 13px; font-weight: 600; text-shadow: 0 1px 3px rgba(0,0,0,0.3); }
.hol-quick-colors { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
.hol-quick-color { width: 28px; height: 28px; border-radius: 50%; border: none; cursor: pointer; transition: transform 0.15s; }
.hol-quick-color:hover { transform: scale(1.2); }

/* Detail panel */
.hol-detail-meta { display: flex; flex-wrap: wrap; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
.hol-detail-meta-item { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-right: 1px solid #e2e8f0; flex: 1; min-width: 120px; }
.hol-detail-meta-item i { font-size: 20px; color: #059669; flex-shrink: 0; }
.hol-meta-label { display: block; font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
.hol-meta-value { display: block; font-size: 13px; color: #1e293b; font-weight: 600; margin-top: 1px; }
.hol-detail-desc { font-size: 14px; color: #475569; line-height: 1.7; margin: 0; padding: 0 20px 16px; }

/* ── Calendar tab layout ── */
.hol-cal-layout { display: grid; grid-template-columns: 1fr 280px; gap: 20px; align-items: start; }
.hol-cal-main { flex: 1; }
.hol-cal-sidebar { display: flex; flex-direction: column; gap: 16px; }
.hol-cal-view-btns { display: flex; gap: 4px; }
.hol-view-btn { padding: 5px 14px; border-radius: 7px; border: 1.5px solid #e2e8f0; background: #f8fafc; color: #64748b; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.hol-view-btn-active { background: #059669; color: #fff; border-color: #059669; }
.hol-upcoming-list { display: flex; flex-direction: column; }
.hol-upcoming-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #f1f5f9; cursor: pointer; transition: background 0.15s; }
.hol-upcoming-item:last-child { border-bottom: none; }
.hol-upcoming-item:hover { background: #f8fafc; }
.hol-upcoming-date { width: 44px; text-align: center; flex-shrink: 0; }
.hol-upcoming-month { display: block; font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
.hol-upcoming-day { display: block; font-size: 22px; font-weight: 800; color: #1e293b; line-height: 1; }
.hol-upcoming-info { flex: 1; min-width: 0; }
.hol-upcoming-name { display: block; font-size: 13px; font-weight: 700; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hol-upcoming-cat { display: block; font-size: 11px; color: #94a3b8; margin-top: 2px; }
.hol-empty-sm { display: flex; flex-direction: column; align-items: center; padding: 30px 16px; color: #94a3b8; text-align: center; gap: 8px; }
.hol-empty-sm i { font-size: 32px; }
.hol-empty-sm p { margin: 0; font-size: 13px; }
.hol-legend-card .hol-legend { padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
.hol-legend-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #475569; }
.hol-legend-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }

/* ── Categories tab layout ── */
.hol-cat-layout { display: grid; grid-template-columns: 1fr 260px; gap: 20px; align-items: start; }
.hol-cat-main { flex: 1; }
.hol-cat-sidebar { display: flex; flex-direction: column; gap: 16px; }
.hol-cat-cards { display: flex; flex-direction: column; gap: 0; }
.hol-cat-card { display: flex; border-bottom: 1px solid #f1f5f9; }
.hol-cat-card:last-child { border-bottom: none; }
.hol-cat-card-accent { width: 4px; flex-shrink: 0; }
.hol-cat-card-body { flex: 1; padding: 14px 18px; }
.hol-cat-card-top { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.hol-cat-card-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.hol-cat-card-info { flex: 1; min-width: 0; }
.hol-cat-card-name { display: block; font-size: 14px; font-weight: 700; color: #1e293b; }
.hol-cat-card-desc { display: block; font-size: 12px; color: #94a3b8; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hol-cat-card-count { display: flex; flex-direction: column; align-items: center; padding: 6px 12px; border-radius: 10px; font-size: 20px; font-weight: 800; line-height: 1; flex-shrink: 0; }
.hol-cat-card-count span { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; margin-top: 2px; }
.hol-cat-bar { height: 4px; background: #f1f5f9; border-radius: 2px; overflow: hidden; margin-bottom: 10px; }
.hol-cat-bar-fill { height: 100%; border-radius: 2px; transition: width 0.4s; }
.hol-cat-card-actions { display: flex; gap: 8px; }
.hol-action-btn span { font-size: 12px; font-weight: 600; }
.hol-dist-list { padding: 14px 16px; display: flex; flex-direction: column; gap: 14px; }
.hol-dist-item { display: flex; flex-direction: column; gap: 4px; }
.hol-dist-header { display: flex; justify-content: space-between; align-items: center; }
.hol-dist-name { font-size: 12px; font-weight: 600; color: #1e293b; }
.hol-dist-count { font-size: 12px; font-weight: 700; color: #475569; }
.hol-dist-bar-wrap { height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
.hol-dist-bar { height: 100%; border-radius: 3px; transition: width 0.4s; }
.hol-dist-pct { font-size: 11px; color: #94a3b8; text-align: right; }

/* ── Statistics tab ── */
.hol-stats-summary { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
.hol-stat-card { background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; padding: 18px 20px; display: flex; align-items: center; gap: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); position: relative; overflow: hidden; }
.hol-stat-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background: var(--c,#059669); }
.hol-stat-icon { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; }
.hol-stat-label { display: block; font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.hol-stat-value { display: block; font-size: 28px; font-weight: 800; color: #1e293b; line-height: 1; }
.hol-stat-sub { display: block; font-size: 11px; color: #94a3b8; margin-top: 3px; }
.hol-stats-layout { display: grid; grid-template-columns: 1fr 260px; gap: 20px; align-items: start; }
.hol-stats-main { flex: 1; }
.hol-stats-sidebar { display: flex; flex-direction: column; gap: 16px; }
.hol-td-center { text-align: center; }
.hol-td-zero { color: #cbd5e1; font-size: 13px; }
.hol-month-name { font-weight: 600; color: #1e293b; }
.hol-pill-hols { background: #fef2f2; color: #ef4444; }
.hol-rate-badge { padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
.hol-rate-has { background: #fef2f2; color: #ef4444; }
.hol-rate-none { background: #f1f5f9; color: #94a3b8; }
.hol-month-bar-wrap { height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; min-width: 80px; }
.hol-month-bar { height: 100%; background: linear-gradient(90deg,#059669,#0d9488); border-radius: 3px; transition: width 0.4s; }
.hol-row-highlight td { background: #fffbeb !important; }
.hol-tfoot td { background: #f8fafc; border-top: 2px solid #e2e8f0; }
.hol-workdays-grid { display: flex; flex-wrap: wrap; gap: 8px; padding: 14px 16px; }
.hol-workday-check { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; border: 1.5px solid #e2e8f0; font-size: 12px; font-weight: 700; color: #64748b; cursor: pointer; transition: all 0.15s; background: #f8fafc; }
.hol-workday-check:hover { border-color: #059669; }
.hol-workday-on { background: #f0fdf4 !important; border-color: #059669 !important; color: #059669 !important; }
.hol-workdays-footer { padding: 0 16px 14px; }
.hol-policy-list { display: flex; flex-direction: column; }
.hol-policy-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; border-bottom: 1px solid #f1f5f9; }
.hol-policy-item:last-child { border-bottom: none; }
.hol-policy-total { background: #f8fafc; }
.hol-policy-label { font-size: 12px; color: #64748b; font-weight: 500; }
.hol-policy-value { font-size: 14px; font-weight: 800; color: #1e293b; }
.hol-policy-blue { color: #2563eb; }
.hol-policy-red { color: #ef4444; }
.hol-policy-green { color: #059669; }

@media (max-width: 1100px) {
  .hol-cal-layout, .hol-cat-layout, .hol-stats-layout { grid-template-columns: 1fr; }
  .hol-stats-summary { grid-template-columns: 1fr 1fr; }
}
</style>

<style src="@/assets/css/hr-page-shared.css"></style>

